/**
 * @file AgencyBoostsPreview.jsx
 * @description "Optional boosts" on the agency landing page, under What Pro adds: the same boost catalog the
 *   Billing page shows (billing/BoostCatalog). Collapsed by default to one line with a chevron (owner, 2026-11-02:
 *   agencies were confused by the open section); a tap opens the full catalog in place. Credit costs come
 *   from getBoostProducts and the per-credit range from getCreditPacks, both over platform_config (the config the
 *   landing page already loads). No button: the plan cards carry the page's call to action.
 * @module components/AgencyBoostsPreview
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import BoostCatalog from "./billing/BoostCatalog";
import { getBoostProducts, getCreditPacks } from "../lib/boostService";
import { getCachedConfig } from "../lib/configService";
import { ChevronDownIcon } from "./Icons";

/** Reads one key from the config the landing page loaded (Map from loadAllConfigs, or a plain object). */
function configValue(config, key) {
  if (!config) return getCachedConfig(key);
  return config instanceof Map ? config.get(key) : config[key];
}

/** Cheapest and dearest price of one credit across the configured packs, in whole dollars. */
export function creditPriceRange(packs) {
  const rates = (packs || []).filter(p => p.credits > 0).map(p => p.price / p.credits);
  if (!rates.length) return null;
  return { min: Math.round(Math.min(...rates)), max: Math.round(Math.max(...rates)) };
}

export default function AgencyBoostsPreview({ isMobile, config }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false); // collapsed until the visitor asks
  const range = creditPriceRange(getCreditPacks(config));
  const products = getBoostProducts(config);
  // One-time credits a new agency gets after onboarding (platform_config, granted by grant_signup_boost_credits).
  const welcome = Number(configValue(config, "boost_signup_grant_credits") ?? 0);
  return (
    <div data-testid="boosts-preview" style={{ maxWidth: 820, margin: isMobile ? "28px auto 0" : "40px auto 0" }}>
      <button
        type="button" data-testid="boosts-toggle" aria-expanded={open} aria-controls="boosts-panel" onClick={() => setOpen(o => !o)}
        style={{ width: "100%", minHeight: 44, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "10px 14px", boxSizing: "border-box", background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 12, cursor: "pointer", fontFamily: "'Inter', sans-serif", fontSize: 14, lineHeight: 1.4, color: "#475569", textAlign: "left" }}
      >
        <span>
          {t("agencyLanding.boostsCollapsed")}{" "}
          <strong style={{ color: "#3730A3", fontWeight: 700 }}>{t(open ? "agencyLanding.boostsHide" : "agencyLanding.boostsShow")}</strong>
        </span>
        <span aria-hidden="true" style={{ flex: "none", display: "inline-flex", transform: open ? "rotate(180deg)" : "none", transition: "transform 0.18s ease" }}>
          <ChevronDownIcon size={18} color="#3730A3" />
        </span>
      </button>
      {open && (
        <div id="boosts-panel" data-testid="boosts-panel" style={{ marginTop: 18 }}>
          <p style={{ textAlign: "center", fontSize: 14, color: "#475569", lineHeight: 1.6, margin: "0 auto 18px", maxWidth: 600 }}>
            {range ? t("agencyLanding.boostsSubtitle", { min: range.min, max: range.max }) : t("agencyLanding.boostsSubtitleNoPrice")}
          </p>
          <div style={{ background: "#F1F5F9", border: "1px solid #E2E8F0", borderRadius: 14, padding: isMobile ? 10 : 14 }}>
            <BoostCatalog t={t} isMobile={isMobile} boostProducts={products} creditRange={range} />
          </div>
          {welcome > 0 && (
            <p data-testid="boosts-welcome" style={{ fontSize: 14, fontWeight: 700, color: "#065F46", margin: "14px auto 0", textAlign: "center" }}>
              {t("agencyLanding.boostsWelcome", { count: welcome })}
            </p>
          )}
          <p data-testid="boosts-reach-note" style={{ fontSize: 14, color: "#475569", lineHeight: 1.6, margin: "14px auto 0", maxWidth: 680, textAlign: "center" }}>
            {t(range ? "agencyLanding.boostsReachNoteDollars" : "agencyLanding.boostsReachNote", {
              invite: products.agency_invite?.credits, direct: products.direct_outreach?.credits,
              inviteMin: (products.agency_invite?.credits || 0) * (range?.min || 0), inviteMax: (products.agency_invite?.credits || 0) * (range?.max || 0),
              directMin: (products.direct_outreach?.credits || 0) * (range?.min || 0), directMax: (products.direct_outreach?.credits || 0) * (range?.max || 0),
            })}
          </p>
        </div>
      )}
    </div>
  );
}
